import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Currency, UsersSelf } from '@freelancer/datastore/collections';
import { Deposits } from '@freelancer/deposits';
import {
  PaymentsErrorType,
  PaymentsMessagingService,
} from '@freelancer/payments-messaging';
import { PaymentsUtils } from '@freelancer/payments-utils';
import { HeadingType } from '@freelancer/ui/heading';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import { PictureDisplay, PictureImage } from '@freelancer/ui/picture';
import { TextSize } from '@freelancer/ui/text';
import {
  DepositMethodApi,
  DepositRequestActionApi,
  DepositRequestApi,
} from 'api-typings/payments/payments';
import * as Rx from 'rxjs';
import { SourceDisplayMode } from '../../../../common/source-template/source-template.component';
import { DepositData, GenericPayload } from '../../../../common/types';
import { parseDepositAPIError } from '../../payments.helpers';

@Component({
  selector: 'app-webmoney-method',
  template: `
    <app-source-template
      [group]="group"
      [sourceRadioValue]="id"
      [sourceDisplayMode]="sourceDisplayMode"
    >
      <app-source-template-header>
        <app-source-template-header-title
          i18n="Webmoney - name of the provider"
        >
          Webmoney
        </app-source-template-header-title>
        <app-source-template-logo
          [image]="webmoneyLogo"
        ></app-source-template-logo>
      </app-source-template-header>
    </app-source-template>
    <!--
      A hidden form to be filled with the result from a successfull POST call to 'payments/0.1/deposits'
    -->
    <form
      #hiddenForm
      class="IsHidden"
      form="hiddenFormGroup"
      action="{{ depositConfig?.wm_config?.api_url }}"
      method="POST"
    >
      <input
        type="hidden"
        flTrackingLabel="webMoneyPayeePurse"
        name="LMI_PAYEE_PURSE"
        value="{{ depositConfig?.wm_config?.payee_purse }}"
      />
      <input
        type="hidden"
        flTrackingLabel="webMoneyResultUrl"
        name="LMI_RESULT_URL"
        value="{{ depositConfig?.wm_config?.result_url }}"
      />
      <input
        type="hidden"
        flTrackingLabel="webMoneySuccessMethod"
        name="LMI_SUCCESS_METHOD"
        value="POST"
      />
      <input
        type="hidden"
        flTrackingLabel="webMoneySuccessUrl"
        name="LMI_SUCCESS_URL"
        value="{{ depositConfig?.wm_config?.success_url }}"
      />
      <input
        type="hidden"
        flTrackingLabel="webMoneyFailMethod"
        name="LMI_FAIL_METHOD"
        value="POST"
      />
      <input
        type="hidden"
        flTrackingLabel="webMoneyFailUrl"
        name="LMI_FAIL_URL"
        value="{{ depositConfig?.wm_config?.fail_url }}"
      />
      <input
        type="hidden"
        flTrackingLabel="webMoneyPaymentId"
        name="PAYMENT_ID"
        value="{{ depositConfig?.invoice?.code }}"
      />
      <input
        type="hidden"
        flTrackingLabel="webMoneyCustomerNumber"
        name="CUST_NUM"
        value="{{ depositConfig?.invoice?.user_id }}"
      />
      <input
        type="hidden"
        flTrackingLabel="webMoneyPaymentNumber"
        name="LMI_PAYMENT_NO"
        value="{{ depositConfig?.invoice?.id }}"
      />
      <input
        type="hidden"
        flTrackingLabel="webMoneyPaymentAmount"
        name="LMI_PAYMENT_AMOUNT"
        value="{{ depositConfig?.invoice?.amount }}"
      />
      <input
        type="hidden"
        flTrackingLabel="webMoneyPaymentDesc"
        name="LMI_PAYMENT_DESC"
        value="{{ depositConfig?.invoice?.description }}"
      />
    </form>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./webmoney-method.component.scss'],
})
export class WebmoneyMethodComponent {
  TextSize = TextSize;
  HeadingType = HeadingType;
  IconColor = IconColor;
  IconSize = IconSize;
  PictureDisplay = PictureDisplay;
  SourceDisplayMode = SourceDisplayMode;

  webmoneyLogo: PictureImage = {
    alt: 'webmoney',
    src: this.paymentsUtils.getMethodLogoPath(DepositMethodApi.WEB_MONEY),
  };
  depositConfig: DepositRequestActionApi;

  @Input() genericPayload: GenericPayload;
  @Input() depositFormGroup: FormGroup;
  @Input() group: FormGroup;
  @Input() id: string;
  @Input() userInfo$: Rx.Observable<UsersSelf>;
  @Input() sourceDisplayMode: SourceDisplayMode;
  @Input() set depositData(depositData: DepositData) {
    if (depositData) {
      this.handleDepositDataChanges(depositData);
    }
  }

  @Output() paymentCurrencySelected = new EventEmitter<Currency>();

  @ViewChild('hiddenForm') hiddenForm: ElementRef<HTMLFormElement>;

  constructor(
    private paymentsMessaging: PaymentsMessagingService,
    private changeDetectorRef: ChangeDetectorRef,
    private deposits: Deposits,
    private paymentsUtils: PaymentsUtils,
  ) {}

  handleDepositDataChanges(depositData: DepositData) {
    const payload: DepositRequestApi = {
      ...this.genericPayload,
      deposit_method: DepositMethodApi.WEB_MONEY,
      amount: depositData.depositAmount,
      currency_id: depositData.paymentCurrency.id,
    };
    this.deposits
      .deposit(payload)
      .toPromise()
      .then(response => {
        if (
          response.status === 'success' &&
          response.result &&
          response.result.wm_config
        ) {
          this.depositConfig = response.result;

          // Manually trigger the change dection. Required to make sure
          // the inputs of the hidden form are set before form submission
          this.changeDetectorRef.detectChanges();

          // Submitting the hidden form to WebMoney
          // for processing (full page redirection)
          this.hiddenForm.nativeElement.submit();
        } else if (response.status === 'error') {
          this.paymentsMessaging.pushError({
            errorCode: response.errorCode,
            errorType: PaymentsErrorType.ERROR,
            eventId: response.requestId,
          });
        }
      })
      .catch(error => {
        this.paymentsMessaging.pushError(parseDepositAPIError(error));
      });
  }
}
